import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { Observable } from 'rxjs';
import { PopupDialog } from './popup/popup.dialog';
import { DialogResult } from './popup/dialog-result';
import { ToastMessageType, ToastService } from './toast';
import { ListTypes } from '@app/Constants';
import { ListViewModel } from '@app/Models/ListViewModel';
import { TenantService } from '@app/Services';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatList, MatListItem } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'list-editor',
    templateUrl: './list.editor.html',
    styleUrls: ['./list.editor.scss'],
    imports: [MatRadioGroup, FormsModule, MatRadioButton, MatTabGroup, MatTab, MatCheckbox, MatButton, MatList, NgClass, MatListItem, MatIcon, MatIconButton, MatFormField, MatInput, MatSelect, MatOption, MatProgressSpinner]
})
export class ListEditorComponent {
  @Input() embedded: boolean;

  listItems: any = null;
  newItemString: string;
  controlType: string;
  newListAttributeName: string;
  listItemErrorString: string;
  listAttributeDefinitionErrorString: string;
  updatingListSelfServiceEnabled = false;
  selectAll = false;
  deletingItem: any = null;

  private list: ListViewModel = null;
  private listAttributeDefinitions: any[];
  private alias: string;

  @Input()
  set listAlias(val: string) {
    this.alias = val;
    this.getList(val);
    this.getListItems(val);
    this.getListAttributeDefinitions(val);
  }

  get listAlias(): string {
    return this.alias;
  }

  get isListDefined(): boolean {
    return this.list !== null && this.list !== undefined;

  }

  constructor(
    private tenantService: TenantService,
    private dialog: MatDialog,
    private toast: ToastService
  ) {
    this.controlType = 'Number';
  }

  onListCanAddChanged(event: MatRadioChange): void {
    const selfServiceEnabled = event.value as boolean;

    let dialogResult: Observable<DialogResult>;
    if (selfServiceEnabled) {
      dialogResult = this.confirmEnableListSelfService();
    } else {
      dialogResult = this.confirmDisableListSelfService();
    }

    dialogResult.subscribe((result) => {
      if (result?.Result === true) {
        this.updatingListSelfServiceEnabled = true;
        this.updateListSelfServiceEnabled(selfServiceEnabled);
      } else {
        this.list.CanAdd = !selfServiceEnabled;
      }
    });
  }

  getList(listAlias: string): void {
    this.tenantService.getList(listAlias).subscribe((res) => {
      this.list = res;
      this.selectAll = false;
    });
  }

  getListItems(listAlias: string): void {
    this.tenantService
      .getListItems([ListTypes.AirswebList, listAlias, 'true'])
      .subscribe((res) => {
        this.listItems = res;
        this.newItemString = '';
      });
  }

  getListAttributeDefinitions(listAlias: string): void {
    this.tenantService
      .getListAttributeDefinitions(listAlias)
      .subscribe((res) => {
        this.listAttributeDefinitions = res;
        this.newListAttributeName = '';
      });
  }

  newItem(): void {
    if (
      this.listItems.find(
        (li) => li.Text.toLowerCase() === this.newItemString.toLowerCase()
      )
    ) {
      this.listItemErrorString = 'Error - Item already exists';
      return;
    }
    if (this.newItemString.length < 1) {
      this.listItemErrorString = 'Error - No new text supplied';
      return;
    }

    this.listItems = null;

    this.tenantService
      .addListItem(ListTypes.AirswebList, this.list.Key, this.newItemString)
      .subscribe(() => {
        this.getListItems(this.list.Key);
      });
  }

  addListAttributeDefinition(): void {
    const name = this.newListAttributeName;
    this.listAttributeDefinitionErrorString = null;

    if (
      this.listAttributeDefinitions.find(
        (lia) => lia.Label.toLowerCase() === name.toLowerCase()
      )
    ) {
      this.listAttributeDefinitionErrorString =
        'Error - Attribute already exists';
      return;
    }
    if (name.length < 1) {
      this.listAttributeDefinitionErrorString = 'Error - No new text supplied';
      return;
    }

    this.listAttributeDefinitions = null;

    this.tenantService
      .addListAttribute(
        this.list.Key,
        this.getNewAttribute(this.controlType, name)
      )
      .subscribe(() => {
        this.getListAttributeDefinitions(this.list.Key);
      });
  }

  setAll(completed: boolean): void {
    this.selectAll = completed;
    if (!this.listItems) {
      return;
    }

    this.listItems.forEach((listItem) => (listItem.Selected = completed));
  }

  areListItems(): boolean {
    return this.listItems && this.listItems.length !== 0;
  }

  updateSelectAll(): void {
    this.selectAll =
      this.listItems && this.listItems.every((listItem) => listItem.Selected);
  }

  isAtLeastOneSelected(): boolean {
    return (
      this.listItems && this.listItems.some((listItem) => listItem.Selected)
    );
  }

  deleteItems(): void {
    if (!this.isAtLeastOneSelected()) {
      return;
    }

    const dialogResult = this.confirmDeleteListItems();
    dialogResult.subscribe((result) => {
      if (result?.Result === true) {
        const selectedListItems = this.listItems.filter(
          (listItem) => listItem.Selected
        );
        this.tenantService
          .deleteListItems(
            ListTypes.AirswebList,
            this.list.Key,
            selectedListItems.map((item) => item.Value)
          )
          .subscribe(() => this.getListItems(this.list.Key));
      }
    });
  }

  deleteItem(item: any) {
    this.listItems = null;
    this.tenantService.deleteListItem(ListTypes.AirswebList, this.list.Key, item.Value)
        .subscribe(() => this.getListItems(this.list.Key));
  }

  setDeleting(item: any) {
    this.deletingItem = item;
  }

  private getNewAttribute(controlType: string, name: string): any {
    return {
      Name: name.replace(' ', ''),
      Translation: name,
      LanguageCode: 'en-gb',
      ControlType: controlType,
      ControlOptions: this.controlOptions(controlType),
      QueryParameters: {},
    };
  }

  private controlOptions(controlType: string): any {
    switch (controlType) {
      case 'Number':
        return { DecimalPlaces: '10' };
      default:
        return {};
    }
  }

  private getSelectedListItemNames(): string {
    let selectedNames = '';
    this.listItems.forEach((listItem) => {
      if (listItem.Selected) {
        selectedNames += listItem.Text + ', ';
      }
    });

    return selectedNames.substring(0, selectedNames.length - 2);
  }

  private getSelectedListItemNumber(): number {
    let selectedListItems = 0;
    this.listItems.forEach((listItem) => {
      if (listItem.Selected) {
        selectedListItems++;
      }
    });

    return selectedListItems;
  }

  private confirmEnableListSelfService(): Observable<DialogResult> {
    const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(
      PopupDialog
    );
    dialogRef.componentInstance.DialogTitle =
      'Enable self-service list admin for EHS users?';
    dialogRef.componentInstance.DialogDescription = `If the tenant has complex state modules set up, they will not be able to add new list items. Check the tenant set up before continuing.

Are you sure you want to enable this functionality?`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Enable';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    return dialogRef.afterClosed();
  }

  private confirmDeleteListItems(): Observable<DialogResult> {
    const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(
      PopupDialog
    );
    dialogRef.componentInstance.DialogTitle =
      'Are you sure you want to delete the selected items?';
    dialogRef.componentInstance.DialogDescription = `You have selected ${this.getSelectedListItemNumber()} items from the list "${
      this.list.FriendlyName
    }" for deletion:\n
    ${this.getSelectedListItemNames()}`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    return dialogRef.afterClosed();
  }

  private confirmDisableListSelfService(): Observable<DialogResult> {
    const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(
      PopupDialog
    );
    dialogRef.componentInstance.DialogTitle =
      'Disable self-service list admin for EHS users?';
    dialogRef.componentInstance.DialogDescription = `The admin user will not be able to add list items in EHS.

Are you sure you want to disable this functionality?`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Disable';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    return dialogRef.afterClosed();
  }

  private updateListSelfServiceEnabled(selfServiceEnabled: boolean): void {
    this.tenantService
      .updateListSelfServiceEnabled(this.list.Key, selfServiceEnabled)
      .subscribe(
        () => {
          this.updatingListSelfServiceEnabled = false;
          this.toast.sendMessage(
            'Success',
            `Self service list admin has been ${
              selfServiceEnabled === true ? 'enabled' : 'disabled'
            } for ${this.list.FriendlyName}`,
            ToastMessageType.success
          );
        },
        () => {
          this.list.CanAdd = !selfServiceEnabled;
          this.updatingListSelfServiceEnabled = false;
          this.toast.sendMessage(
            'Failure',
            `Failed to ${
              selfServiceEnabled === true ? 'enable' : 'disable'
            } self service list admin for ${this.list.FriendlyName}`,
            ToastMessageType.error
          );
        }
      );
  }
}
